import React, { useState, useEffect } from 'react';
import './GamePage.css';

function GamePage({ gameData, isHost, socket, playerName, onRevealGame, onNewGame, onDisconnect }) {
  const isImposter = gameData.type === 'imposter';
  const isPlayer = gameData.type === 'player';
  const isSpectator = gameData.type === 'spectator';
  const isRevealed = gameData.revealed;
  const startingPlayer = gameData.startingPlayer;
  const allPlayers = gameData.allPlayers || [];
  const voteCounts = gameData.voteCounts || {};
  
  // State to control word/hint visibility
  const [showWord, setShowWord] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);
  const [selectedVote, setSelectedVote] = useState(null);

  // Get current player name from gameData or props
  const currentPlayerName = gameData.playerName || playerName;
  
  // Update selectedVote when votes change - find which player the current user voted for
  useEffect(() => {
    if (currentPlayerName && (gameData.voterToTarget || gameData.votes)) {
      let newSelectedVote = null;
      
      // First try to use voterToTarget map (more efficient) - uses player name now
      if (gameData.voterToTarget && gameData.voterToTarget[currentPlayerName]) {
        newSelectedVote = gameData.voterToTarget[currentPlayerName];
      } 
      // Fallback to searching votes object
      else if (gameData.votes) {
        // Find which player has the current user's name in their votes
        const currentUserVote = Object.keys(gameData.votes).find(playerId => {
          return gameData.votes[playerId] && Array.isArray(gameData.votes[playerId]) && gameData.votes[playerId].includes(currentPlayerName);
        });
        if (currentUserVote) {
          newSelectedVote = currentUserVote;
        }
      }
      
      // Only update if we found a vote that's different from current
      // This prevents unnecessary updates and preserves state when vote data is temporarily missing
      if (newSelectedVote !== null && newSelectedVote !== selectedVote) {
        setSelectedVote(newSelectedVote);
      }
      // Don't clear selectedVote if user's vote isn't found - preserve the state
      // This prevents clearing when component re-renders due to showWord changes or other UI updates
    }
  }, [currentPlayerName, gameData.votes, gameData.voterToTarget, selectedVote]);

  const handleLeaveClick = () => {
    setShowLeaveConfirm(true);
  };

  const handleLeaveConfirm = () => {
    setShowLeaveConfirm(false);
    onDisconnect();
  };

  const handleRevealClick = () => {
    setShowRevealConfirm(true);
  };

  const handleRevealConfirm = () => {
    setShowRevealConfirm(false);
    onRevealGame();
  };

  const handleVote = (targetPlayerId, targetPlayerName) => {
    if (socket && targetPlayerId && targetPlayerName) {
      // Allow changing vote - server will automatically remove old vote and add new one
      // Send both ID and name so server can find player even if ID is stale after reconnection
      socket.emit('vote', { targetPlayerId, targetPlayerName });
      // Optimistically update UI - will be confirmed when votes-updated event arrives
      setSelectedVote(targetPlayerId);
    }
  };

  return (
    <div className="game-page">
      <div className="game-container">
        <div className="game-header">
          <h2>Game Active</h2>
          <button onClick={handleLeaveClick} className="disconnect-btn">Leave</button>
        </div>

        {startingPlayer && !isRevealed && (
          <div className="starting-player-announcement">
            <p className="starting-player-text">
              🎲 <strong>{startingPlayer}</strong> starts the game!
            </p>
          </div>
        )}

        {!isRevealed && (
          <div className="game-content">
            {!showWord ? (
              <div className="reveal-prompt">
                <p className="reveal-prompt-text">Ready to see your role?</p>
                <button 
                  onClick={() => setShowWord(true)} 
                  className="show-word-btn"
                >
                  Show My Role
                </button>
              </div>
            ) : (
              <>
                {isImposter && (
                  <div className="imposter-card">
                    <h1 className="role-title">🎭 YOU ARE THE IMPOSTER</h1>
                    <div className="info-box">
                      <p><strong>Category:</strong> {gameData.category}</p>
                      {gameData.hint ? (
                        <p><strong>Hint:</strong> <span className="hint-display">{gameData.hint}</span></p>
                      ) : (
                        <p style={{ fontStyle: 'italic', color: '#991b1b', opacity: 0.7 }}>No hint available</p>
                      )}
                      <p className="hint-text">Try to blend in and guess the word!</p>
                      <button 
                        onClick={() => setShowWord(false)} 
                        className="hide-word-btn"
                      >
                        Hide Role
                      </button>
                    </div>
                  </div>
                )}

                {isPlayer && (
                  <div className="player-card">
                    <h1 className="role-title">✅ You're a Player</h1>
                    <div className="info-box">
                      <p><strong>Your Word:</strong></p>
                      <p className="word-display">{gameData.word}</p>
                      <p><strong>Category:</strong> {gameData.category}</p>
                      <p className="hint-text">Give clues without revealing your word!</p>
                      <button 
                        onClick={() => setShowWord(false)} 
                        className="hide-word-btn"
                      >
                        Hide Role
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {isSpectator && (
              <div className="spectator-card">
                <h1 className="role-title">👀 Spectator</h1>
                <div className="info-box">
                  <p>You're watching this game.</p>
                  <p><strong>Category:</strong> {gameData.category}</p>
                </div>
              </div>
            )}

            {/* Voting Section - Hidden for now */}
            {/* {allPlayers.length > 0 && (
              <div className="voting-section">
                <h3 className="voting-title">Vote for Imposter</h3>
                <div className="voting-list">
                  {allPlayers.map(player => (
                    <button
                      key={player.id}
                      onClick={() => handleVote(player.id, player.name)}
                      className={`vote-btn ${selectedVote === player.id ? 'selected' : ''}`}
                    >
                      <span className="vote-player-name">{player.name}</span>
                      {voteCounts[player.id] && voteCounts[player.id] > 0 && (
                        <span className="vote-count">{voteCounts[player.id]}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )} */}

            {isHost && (
              <div className="host-actions">
                <button onClick={handleRevealClick} className="reveal-btn">
                  Reveal Imposter & Words
                </button>
              </div>
            )}
          </div>
        )}

        {isRevealed && (
          <div className="revealed-content">
            <h2 className="revealed-title">Game Revealed!</h2>
            
            <div className="imposters-section">
              <h3>Imposters:</h3>
              <div className="imposter-list">
                {gameData.revealed.imposters.map(imposterId => {
                  const player = gameData.revealed.words[imposterId];
                  if (!player) {
                    console.warn('Imposter not found in words:', imposterId);
                    return null;
                  }
                  return (
                    <div key={imposterId} className="imposter-reveal">
                      <span className="imposter-name">{player.name || 'Unknown'}</span>
                      <span className="imposter-badge">IMPOSTER</span>
                      {player.hint && <span className="imposter-hint">Hint: {player.hint}</span>}
                    </div>
                  );
                }).filter(Boolean)}
              </div>
            </div>

            <div className="words-section">
              <h3>All Words:</h3>
              <div className="words-list">
                {Object.entries(gameData.revealed.words || {}).map(([playerId, data]) => {
                  if (!data) {
                    console.warn('Word data missing for player:', playerId);
                    return null;
                  }
                  const votes = gameData.revealed.votes?.[playerId] || 0;
                  return (
                    <div key={playerId} className={`word-item ${data.isImposter ? 'imposter' : ''}`}>
                      <span className="player-name">{data.name || 'Unknown'}:</span>
                      <span className="word">{data.word || 'N/A'}</span>
                      {/* {votes > 0 && (
                        <span className="vote-badge">🗳️ {votes} vote{votes !== 1 ? 's' : ''}</span>
                      )} */}
                    </div>
                  );
                }).filter(Boolean)}
              </div>
            </div>

            {isHost && (
              <div className="host-actions">
                <button onClick={onNewGame} className="primary-btn">
                  New Game
                </button>
              </div>
            )}
          </div>
        )}

        {/* Leave Confirmation Modal */}
        {showLeaveConfirm && (
          <div className="modal-overlay" onClick={() => setShowLeaveConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Leave Game?</h3>
              <p>Are you sure you want to leave the game? You won't be able to rejoin this game.</p>
              <div className="modal-actions">
                <button onClick={() => setShowLeaveConfirm(false)} className="modal-btn cancel">
                  Cancel
                </button>
                <button onClick={handleLeaveConfirm} className="modal-btn confirm">
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reveal Confirmation Modal */}
        {showRevealConfirm && (
          <div className="modal-overlay" onClick={() => setShowRevealConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Reveal Game?</h3>
              <p>Are you sure you want to reveal the imposter and all words? This will end the game for everyone.</p>
              <div className="modal-actions">
                <button onClick={() => setShowRevealConfirm(false)} className="modal-btn cancel">
                  Cancel
                </button>
                <button onClick={handleRevealConfirm} className="modal-btn confirm">
                  Reveal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GamePage;

